/**
 * Utility functions for API requests with authentication
 * 
 * This module provides utilities for making authenticated API requests with JWT tokens.
 * All API calls should use these utilities to ensure proper authentication headers are included.
 * 
 * Usage Examples:
 * 
 * 1. Using createAuthenticatedRequestOptions in services:
 *    ```typescript
 *    import { createAuthenticatedRequestOptions } from '../utils/apiUtils';
 *    
 *    const response = await fetch('/api/endpoint', createAuthenticatedRequestOptions({
 *      method: 'POST',
 *      headers: { 'Content-Type': 'application/json' },
 *      body: JSON.stringify(data)
 *    }));
 *    ```
 * 
 * 2. Using useAuthenticatedApi hook in components:
 *    ```typescript
 *    import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
 *    
 *    const MyComponent = () => {
 *      const { makeAuthenticatedRequest, isAuthenticated } = useAuthenticatedApi();
 *      
 *      const handleApiCall = async () => {
 *        if (!isAuthenticated) return;
 *        
 *        try {
 *          const response = await makeAuthenticatedRequest('/api/endpoint', {
 *            method: 'POST',
 *            headers: { 'Content-Type': 'application/json' },
 *            body: JSON.stringify(data)
 *          });
 *          const result = await response.json();
 *        } catch (error) {
 *          // console.error('API call failed:', error);
 *        }
 *      };
 *    };
 *    ```
 */

/**
 * Gets the current authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('bri_token');
};

/**
 * Creates request options with authentication headers and cache control
 */
export const createAuthenticatedRequestOptions = (options: RequestInit = {}): RequestInit => {
  const defaultHeaders: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  };

  // Add Authorization header if token exists
  const token = getAuthToken();
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  return {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {})
    }
  };
};

/**
 * Makes an authenticated API request with automatic token refresh on 401
 */
export const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const requestOptions = createAuthenticatedRequestOptions(options);
  
  let response = await fetch(url, requestOptions);
  
  // If we get a 401, the token might be expired
  // In a real application, you might want to implement token refresh logic here
  // For now, we'll just return the response and let the calling code handle it
  
  return response;
}; 